<template>
  <div>
    <div class="image-header">
      <div class="compare" v-if="compare">
        <v-btn icon small @click="backToRace">
          <v-icon size="18">$vuetify.icons.chevron-left</v-icon>
        </v-btn>
        {{ compare.images[0]["0"].race_event }}
        <div class="divider"></div>
        <div class="statistics-col">
          <div>Auto Save</div>
          <div class="auto-save">
            <v-switch
              v-model="overlay.autoSave"
              class="mt-0"
              dense
              hide-details
              :disabled="overlay.loading"
              :label="(overlay.autoSave && 'On') || ''"
              :ripple="false"
            />
            <v-btn
              color="primary"
              v-if="!overlay.autoSave"
              class="pa-2"
              depressed
              small
              :disabled="overlay.loading || !isActiveSaveBtn"
              @click="saveImage"
            >
              <v-icon left size="16">$vuetify.icons.save</v-icon>
              Save
            </v-btn>
          </div>
        </div>
        <div class="divider"></div>
        <template v-if="isOverlayModeAvailable && isSegmentationAvailable">
          <div class="statistics-col">
            <div>Segmentation</div>
            <v-switch
              v-model="overlay.active"
              class="mt-0"
              v-if="overlay.id"
              dense
              hide-details
              :disabled="!overlay.id || overlay.loading || isCanvasImgLoading"
              :label="overlay.active ? 'On' : 'Off'"
              :ripple="false"
            />
            <v-btn
              color="primary"
              v-else
              depressed
              small
              :disabled="overlay.loading"
              @click="applySegmentation"
            >
              Apply
            </v-btn>
          </div>
          <div class="divider"></div>
          <template v-if="overlayToolbarProps">
            <div class="statistics-col">
              <div>Overlay image</div>
              <v-radio-group
                class="mt-0"
                row
                dense
                hide-details
                :disabled="overlay.autoSave && isActiveSaveBtn"
                :value="overlayToolbarProps.selectedImage.value"
                @change="overlayToolbarProps.selectedImage.handler"
              >
                <v-radio
                  v-for="v in overlayToolbarProps.selectedImage.items"
                  class="overlay-images"
                  :key="v.id"
                  :label="v.label"
                  :value="v.id"
                ></v-radio>
              </v-radio-group>
            </div>
            <div
              v-for="(filter, i) in overlayToolbarProps.filters"
              :key="i"
              class="filter-divider"
            >
              <div class="divider"></div>
              <div>
                <div class="menu-item-header">
                  <v-icon size="16">{{ filter.icon }}</v-icon>
                  {{ filter.label }}
                </div>
                <v-slider
                  color="var(--v-grey-darken-1)"
                  hide-details
                  max="1"
                  min="0"
                  track-color="var(--v-grey-lighten-3)"
                  track-fill-color="var(--v-grey-lighten-1)"
                  :step="0.05"
                  :value="filter.value"
                  @change="filter.handler"
                />
              </div>
            </div>
          </template>
        </template>
      </div>
      <loader v-else size="24" />
      <div class="tags">
        <div class="divider"></div>
        <v-btn icon class="statistics-col" @click="changeIsShowInfoAside">
          <div class="icon-wrapper">
            <v-icon size="20">$vuetify.icons.clipboard</v-icon>
            <span>Tags</span>
          </div>
        </v-btn>
      </div>
    </div>
    <div class="image-content">
      <ImageToolBar
        @changeOption="changeOption"
        :actions="actions"
        :options="options"
        :overlay="overlayToolbarProps"
      />
      <ImageCanvas
        v-if="compare"
        ref="canvas"
        :options="options"
        :images="images"
        :savedLines="compare.lines"
        :isBaseLine="compare.baseline_mode"
        :overlay="overlayCanvasProps"
        :selectedImageIndex="selectedImageIndex"
        @updateSaveButton="updateSaveButton"
        @changeMode="changeOption"
        @saveImage="updateImage"
        @setCanvasImageLoading="setCanvasImageLoading"
      />
      <CompareInfo
        class="aside-panel"
        v-if="isShowInfoAside"
        :images="images"
        :selectedItem="
          overlayToolbarProps && overlayToolbarProps.selectedImage
            ? overlayToolbarProps.selectedImage.label
            : ''
        "
        @reDrawImg="reDrawImg"
        @change="changeSelectedImage"
      />
      <loader
        v-if="overlay.loading"
        absolute
        overlay-background="rgb(128 128 128 / 30%)"
        size="44"
      />
    </div>
    <save-comparison-dialog
      :visible="saveVisible"
      @update:visible="saveVisible = false"
      @handleLeavePage="handleLeavePage"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { defaultImageFilters } from "@/constants";
import utils, { debounce } from "@/utils/utils";
import ImageCanvas from "../../components/ImageCanvas";
import ImageToolBar from "../../components/imageToolBar";
import Loader from "@/components/Loader";
import { getCompare, putCompare } from "../../api/compares";
import {
  getOverlay,
  sendCompareToSegmentation,
  updateOverlay,
} from "../../api/overlays";
import CompareInfo from "./compareInfo";
import SaveComparisonDialog from "@/components/dialogs/SaveComparisonDialog";

const overlayStatuses = {
  done: "done",
  error: "error",
  pending: "in_progress",
};

export default {
  name: "Compare",
  components: {
    ImageCanvas,
    ImageToolBar,
    Loader,
    CompareInfo,
    SaveComparisonDialog,
  },
  data() {
    return {
      compare: null,
      saveVisible: false,
      pendingNavigation: null,
      isCanvasImgLoading: false,
      images: [],
      options: {
        mode: "single",
        lineWidth: 5,
        lineColor: "red",
        sketchType: "pencil",
        bBox: false,
      },
      overlay: {
        active: false,
        activeImgId: null,
        id: null,
        images: [],
        lines: [],
        loading: false,
        opacity: 1,
        imageOpacity: 0.5,
        autoSave: false,
      },
      baseAPIURL: process.env.VUE_APP_BASE_API_URL,
      isShowInfoAside: false,
      isActiveSaveBtn: false,
      selectedImageIndex: -1,
    };
  },
  computed: {
    actions() {
      return [
        {
          icon: "$vuetify.icons.undo",
          handler: this.undoLine,
          tooltip: "Undo",
        },
        {
          icon: "$vuetify.icons.download",
          handler: () => utils.downloadFile(this.downnloadCompareHref),
          tooltip: "Download",
        },
      ];
    },
    compareId() {
      return this.$route.params.compareId;
    },
    downnloadCompareHref() {
      return `${this.baseAPIURL}download/compares/${this.compareId}`;
    },
    hasOverlayImages() {
      return !!this.overlay?.images?.length;
    },
    isOverlayModeAvailable() {
      return (
        process.env.VUE_APP_IS_AVAILABLE_COMPARISON_OVERLAY_MODE === "true"
      );
    },
    isSegmentationAvailable() {
      return this.compare?.images?.length === 1;
    },
    overlayCanvasProps() {
      return {
        active: this.overlay.active && this.hasOverlayImages,
        activeImgId: this.overlay.activeImgId,
        images: this.overlay.images,
        lines: this.overlay.lines,
        opacity: this.overlay.opacity,
        imageOpacity: this.overlay.imageOpacity,
        loading: this.overlay.loading,
      };
    },
    overlayImages() {
      return this.images.map((el, i) => ({
        id: el._id,
        label: el?.tags[0]?.car_number || i,
      }));
    },
    overlayToolbarProps() {
      if (!this.overlay.active || !this.hasOverlayImages) {
        return null;
      }
      return {
        filters: [
          {
            handler: (v) => (this.overlay.opacity = v),
            icon: "$vuetify.icons.tool-box",
            label: "Outline opacity",
            value: this.overlay.opacity,
          },
          {
            handler: (v) => (this.overlay.imageOpacity = v),
            icon: "$vuetify.icons.picture",
            label: "Image opacity",
            value: this.overlay.imageOpacity,
          },
        ],
        selectedImage: {
          handler: (v) => (this.overlay.activeImgId = v),
          items: this.overlayImages,
          value: this.overlay.activeImgId,
          label: this.overlayImages.find(
            (item) => item.id !== this.overlay.activeImgId
          ).label,
        },
      };
    },
  },
  watch: {
    "overlay.active"(value) {
      if (value) {
        this.options.bBox = false;
        if (!this.hasOverlayImages) {
          this.getSegmentation();
        }
      }
    },
    "overlay.autoSave"(isAutoSave) {
      if (isAutoSave && this.isActiveSaveBtn) {
        this.saveImage();
      }
    },
    isActiveSaveBtn(isImageUpdated) {
      if (this.overlay.autoSave && isImageUpdated) {
        this.debouncedSaveImage();
      }
    },
  },
  methods: {
    ...mapActions({
      getInfo: "engine/getInfo",
      notificationError: "notification/error",
      notificationSucces: "notification/success",
    }),
    async applySegmentation() {
      try {
        this.overlay.loading = true;
        const { data } = await sendCompareToSegmentation(this.compareId);
        if (!data?.overlay_id) {
          throw new Error();
        }
        this.overlay.id = data?.overlay_id ?? null;
        await this.getSegmentation();
      } catch (e) {
        this.notificationError("Failed to start the segmentation.");
        console.log(e);
      } finally {
        this.overlay.loading = false;
      }
    },
    async getSegmentation() {
      if (!this.overlay.id) {
        return null;
      }
      try {
        this.overlay.loading = true;
        const { data } = await getOverlay(this.overlay.id);
        if (!data?.images || data?.status === overlayStatuses.error) {
          throw new Error();
        }
        if (data?.status === overlayStatuses.pending) {
          await utils.timeout(3000);
          return this.getSegmentation();
        }

        const tempImages = Object.values(data.images[0]);
        const overlayPromise = tempImages.map((image) =>
          utils.loadImage(image.overlay_url)
        );
        const overlayImages = await Promise.all(overlayPromise);
        const images = tempImages.map((el, i) => ({
          ...el,
          imageEl: overlayImages[i],
        }));
        const initialImg = images.find(
          (el) => el?.overlay_info?.position?.x || el?.overlay_info?.position?.y
        );
        this.overlay.active = true;
        this.overlay.activeImgId = initialImg?._id || images[1]?._id;
        this.overlay.images = images;
        this.overlay.lines = data.lines;
        this.overlay.loading = false;
      } catch (e) {
        console.log(e);
        this.overlay.loading = false;
        this.overlay.active = false;
        this.notificationError("Failed to retrieve the segmentation info.");
      }
    },
    init() {
      return getCompare(this.compareId).then((res) => {
        this.compare = res.data;
        this.overlay.id = res.data?.overlay_id ?? null;
        this.setImages(res.data?.images || []);
      });
    },
    backToRace() {
      this.$router.go(-1);
    },
    async saveComparison(compare) {
      try {
        this.overlay.loading = true;
        await putCompare(this.compareId, compare);
        await this.init();
        this.notificationSucces("The comparison was updated.");
      } catch (e) {
        this.notificationError("Failed to update the comparison.");
        console.log(e);
      } finally {
        this.overlay.loading = false;
      }
    },
    async saveOverlay(payload) {
      try {
        await updateOverlay(this.overlay.id, payload);
        this.overlay.lines = [...payload.lines];
        const message = this.overlay.autoSave
          ? "The overlay was auto-saved"
          : "The overlay was updated.";
        this.notificationSucces(message);
      } catch (e) {
        this.notificationError("Failed to update the overlay.");
        console.log(e);
      }
    },
    updateImage(payload) {
      if (this.overlay.active) {
        this.saveOverlay(payload);
      } else {
        this.saveComparison(payload);
      }
    },
    changeIsShowInfoAside() {
      this.isShowInfoAside = !this.isShowInfoAside;
    },
    reDrawImg() {
      this.$refs.canvas.reDrawImg();
    },
    setCanvasImageLoading(isLoading) {
      this.isCanvasImgLoading = isLoading;
    },
    saveImage() {
      if (this.$refs.canvas.updateImage) {
        this.$refs.canvas.updateImage();
      }
    },
    updateSaveButton(value) {
      this.isActiveSaveBtn = value;
    },
    debouncedSaveImage: debounce(function () {
      this.saveImage();
    }, 1000),
    setImages(newImages) {
      const prevFilters = this.images.reduce((res, cur) => {
        res[cur.index] = cur.filters;
        return res;
      }, {});

      this.images = newImages.reduce((res, el, indexOuter) => {
        const imagesBuf = Object.values(el).map((el, indexInner) => {
          const index = indexOuter * 2 + indexInner;
          const curPrevFilters = prevFilters[index];
          return {
            ...el,
            filters: curPrevFilters
              ? { ...curPrevFilters }
              : { ...defaultImageFilters },
            index: indexOuter * 2 + indexInner,
          };
        });
        res.push(...imagesBuf);
        return res;
      }, []);
    },
    changeSelectedImage(index) {
      if (this.selectedImageIndex === index) {
        this.selectedImageIndex = -1;
      } else {
        this.selectedImageIndex = index;
      }
    },
    undoLine() {
      this.$refs.canvas.undoLine();
    },
    changeOption(option, value) {
      this.options[option] = value;
    },
    async handleLeavePage() {
      if (this.pendingNavigation) {
        this.pendingNavigation();
      }
    },
  },
  mounted() {
    this.init();
  },
  beforeRouteLeave(to, from, next) {
    if (this.isActiveSaveBtn) {
      this.saveVisible = true;
      this.pendingNavigation = next;
      return false;
    } else {
      next();
    }
  },
};
</script>

<style lang="scss" scoped>
.image-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: var(--v-blue-lighten-4);
  box-shadow: inset 0px -1px 0px var(--v-blue-lighten-2);
  color: var(--v-primary-base);
  padding: 2px 16px;
  --block-height: 40px;
  min-height: 52px;
  .compare {
    height: var(--block-height);
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .tags {
    display: flex;
    gap: 16px;
    height: var(--block-height);
    align-items: center;
    .icon-wrapper {
      display: flex;
      flex-direction: column;
      align-items: center;
    }
  }
}

.filter-divider {
  display: flex;
  gap: 16px;
  padding-top: 4px;
}

.divider {
  border-right: 1px solid var(--v-blue-lighten-2);
  height: var(--block-height);
  margin: 4px 0px;
}

.overlay-images:last-child {
  margin-right: 0px;
}

.image-content {
  position: relative;
  display: flex;
  flex: 1;
}

.aside-panel {
  position: absolute;
  top: 0;
  right: 0;
  background: white;
  z-index: 3;
}

.statistics-col {
  display: flex;
  height: var(--block-height);
  flex-direction: column;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--v-grey-darken-1);
}

.auto-save {
  display: flex;
  gap: 6px;
}

.menu-item-header {
  --picture-stroke-color: var(--v-secondary-base);
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--v-grey-darken-1);
  margin-bottom: 6px;
}

::v-deep {
  .v-btn--icon {
    width: 32px;
    height: 32px;
  }

  .overlay-images {
    .v-radio {
      min-height: 36px;
      border-top: 1px solid var(--v-blue-lighten-3);
    }
  }

  .v-slider {
    min-height: 20px;
    margin: 0;
  }

  .v-slider__thumb {
    background-color: white !important;
    border: 2px solid var(--v-grey-darken-1);
    cursor: pointer;

    &:before {
      transform: scale(0);
      left: -14px;
      top: -14px;
    }

    &:after {
      width: 16px;
      height: 16px;
    }
  }

  .v-slider__thumb-container--active .v-slider__thumb::before {
    transform: scale(1) !important;
  }

  .v-slider__thumb-container:hover .v-slider__thumb::before {
    transform: scale(0.7);
  }
}
</style>
